/* eslint-disable react/no-unescaped-entities */
/**
 * /hustleup/groups — mobile-first browse + create groups.
 * /hustleup/groups/new — create form.
 * /hustleup/groups/:slug — detail with members + events + RSVP.
 *
 * Single file for context efficiency.  Each export is its own route.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Users, Calendar, MapPin, ArrowLeft, Plus, Star, Loader2,
         Clock, ExternalLink, CheckCircle2, Hourglass } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API });
const TKN = () => localStorage.getItem("hu_session_token");

const css = `
  .hg{min-height:100vh;background:#fffef5;color:#0f172a;font-family:Inter,sans-serif}
  .hg-top{display:flex;justify-content:space-between;align-items:center;
          padding:14px 18px;background:#0f172a;color:#fbbf24;position:sticky;top:0;z-index:30}
  .hg-back{color:#fbbf24;text-decoration:none;display:inline-flex;align-items:center;gap:6px;font-size:13px}
  .hg-cta{background:#fbbf24;color:#0f172a;border:none;padding:8px 18px;border-radius:999px;
          font-weight:700;font-size:13px;cursor:pointer;display:inline-flex;align-items:center;gap:6px}
  .hg-main{max-width:680px;margin:0 auto;padding:24px 18px 80px}
  .hg-h1{font-family:Georgia,serif;font-size:30px;font-weight:800;margin:0 0 6px;letter-spacing:-0.025em}
  .hg-sub{color:#475569;font-size:14px;margin:0 0 24px;line-height:1.5}
  .hg-card{background:white;border:1px solid #e8e2d4;border-radius:14px;padding:18px;margin-bottom:14px;cursor:pointer;text-align:left;width:100%;font:inherit;color:inherit;transition:transform .15s,box-shadow .15s}
  .hg-card:hover{transform:translateY(-2px);box-shadow:0 12px 30px rgba(15,23,42,.08)}
  .hg-cname{font-size:17px;font-weight:800;letter-spacing:-0.01em;margin-bottom:4px}
  .hg-cmeta{font-size:12px;color:#64748b;display:flex;align-items:center;gap:6px;margin-bottom:8px}
  .hg-cdesc{font-size:13px;color:#475569;line-height:1.5;margin-bottom:10px}
  .hg-cfooter{display:flex;justify-content:space-between;align-items:center;font-size:12px;color:#0f172a;font-weight:600}
  .hg-pill{background:#fef3c7;color:#92400e;padding:3px 10px;border-radius:999px;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.06em}
  .hg-input,.hg-area,.hg-sel{width:100%;padding:12px 14px;border:1px solid #e8e2d4;border-radius:10px;font-size:15px;outline:none;font-family:inherit;background:white;margin-bottom:14px;box-sizing:border-box}
  .hg-area{min-height:120px;resize:vertical}
  .hg-lbl{font-size:11px;font-weight:700;color:#64748b;text-transform:uppercase;letter-spacing:.06em;display:block;margin-bottom:5px}
  .hg-btn{width:100%;padding:14px 18px;background:#0f172a;color:#fbbf24;border:none;border-radius:12px;font-weight:700;font-size:15px;cursor:pointer;display:inline-flex;align-items:center;justify-content:center;gap:8px}
  .hg-err{background:#fee2e2;color:#991b1b;padding:10px 14px;border-radius:8px;font-size:13px;margin-top:12px}
  .hg-event{background:#fffbeb;border:1px solid #fde68a;border-radius:12px;padding:16px;margin-bottom:12px}
  .hg-attlist{display:flex;flex-wrap:wrap;gap:8px;margin-top:12px}
  .hg-avatar{width:36px;height:36px;border-radius:50%;background:#fbbf24;color:#0f172a;display:inline-flex;align-items:center;justify-content:center;font-weight:800;font-size:14px}
  .hg-tabs{display:flex;gap:6px;background:#fef3c7;padding:4px;border-radius:999px;margin-bottom:18px}
  .hg-tab{flex:1;background:transparent;border:none;padding:8px 0;border-radius:999px;font-weight:700;cursor:pointer;color:#92400e;font-size:13px}
  .hg-tab.active{background:#0f172a;color:#fbbf24}
  .hg-fav{background:transparent;border:none;cursor:pointer;color:#cbd5e1;padding:6px}
  .hg-fav.active{color:#f59e0b}
  @media (min-width:760px){.hg-main{max-width:880px}}
`;

// ────────── Browse ──────────
export function HustleUpGroupsPage() {
  const navigate = useNavigate();
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    const t = setTimeout(() => {
      setLoading(true);
      api.get("/api/hustleup/groups", { params: q ? { q, limit: 60 } : { limit: 60 } })
        .then(r => { setGroups(r.data.results || []); setLoading(false); });
    }, 280);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <div className="hg" data-testid="hu-groups-root">
      <header className="hg-top">
        <a href="/hustleup" className="hg-back"><ArrowLeft size={14} /> HustleUp</a>
        <button onClick={() => navigate("/hustleup/groups/new")}
                className="hg-cta" data-testid="hu-groups-new">
          <Plus size={14} /> New group
        </button>
      </header>
      <main className="hg-main">
        <h1 className="hg-h1">Groups near you</h1>
        <p className="hg-sub">
          Find a tribe or start your own. 100% free for members and
          organisers — no monthly fees, no organiser charges, ever.
        </p>
        <input className="hg-input" placeholder="Search groups…"
                value={q} onChange={e => setQ(e.target.value)}
                data-testid="hu-groups-search" />

        {loading
          ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
              <Loader2 className="spin" /> Loading…
            </div>
          : groups.length === 0
          ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
              No groups yet — be the first to start one!
            </div>
          : groups.map(g => (
              <button key={g.slug} className="hg-card"
                      onClick={() => navigate(`/hustleup/groups/${g.slug}`)}
                      data-testid={`hu-grp-${g.slug}`}>
                <div className="hg-cname">{g.name}</div>
                <div className="hg-cmeta">
                  <MapPin size={11} /> {g.region}
                  <span style={{ color: "#cbd5e1" }}>•</span>
                  <span className="hg-pill">{g.category}</span>
                </div>
                <div className="hg-cdesc">
                  {(g.description || "").slice(0, 160)}
                  {g.description?.length > 160 ? "…" : ""}
                </div>
                <div className="hg-cfooter">
                  <span><Users size={12} /> {g.member_count} member{g.member_count === 1 ? "" : "s"}</span>
                  <span>{(g.hosts || []).length} host{(g.hosts || []).length === 1 ? "" : "s"}</span>
                </div>
              </button>
            ))}
      </main>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ────────── Create ──────────
export function HustleUpGroupCreatePage() {
  const navigate = useNavigate();
  const [cats, setCats] = useState([]);
  const [name, setName] = useState("");
  const [category, setCategory] = useState("");
  const [region, setRegion] = useState("");
  const [postcode, setPostcode] = useState("");
  const [description, setDescription] = useState("");
  const [tags, setTags] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!TKN()) { navigate("/hustleup/join"); return; }
    api.get("/api/hustleup/categories").then(r => setCats(r.data || []));
  }, [navigate]);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post(`/api/hustleup/groups?token=${TKN()}`, {
        name, category, region, postcode: postcode || null,
        description, tags: tags.split(",").map(t => t.trim()).filter(Boolean),
        topics: [],
      });
      navigate(`/hustleup/groups/${r.data.slug}`);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || e2.message); setBusy(false);
    }
  };

  return (
    <div className="hg" data-testid="hu-group-create-root">
      <header className="hg-top">
        <a href="/hustleup/groups" className="hg-back"><ArrowLeft size={14} /> Groups</a>
        <div style={{ color: "white", fontWeight: 700 }}>Start a group</div>
      </header>
      <main className="hg-main">
        <h1 className="hg-h1">Spin up your group</h1>
        <p className="hg-sub">60 seconds. Always free. You'll be the lead
          organiser — invite co-hosts whenever you like.</p>
        <form onSubmit={submit}>
          <label className="hg-lbl">Group name</label>
          <input className="hg-input" required minLength={4} value={name}
                  onChange={e => setName(e.target.value)} data-testid="hu-gc-name"
                  placeholder="e.g. Brisbane Sunset Hikers" />

          <label className="hg-lbl">Category</label>
          <select className="hg-sel" required value={category}
                  onChange={e => setCategory(e.target.value)} data-testid="hu-gc-cat">
            <option value="">Choose a category…</option>
            {cats.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>

          <label className="hg-lbl">Region / suburb</label>
          <input className="hg-input" required value={region}
                  onChange={e => setRegion(e.target.value)} data-testid="hu-gc-region"
                  placeholder="e.g. Inner Brisbane" />

          <label className="hg-lbl">Postcode (optional)</label>
          <input className="hg-input" maxLength={4} value={postcode}
                  onChange={e => setPostcode(e.target.value)} data-testid="hu-gc-pc" />

          <label className="hg-lbl">Description (min 20 chars)</label>
          <textarea className="hg-area" required minLength={20} value={description}
                    onChange={e => setDescription(e.target.value)}
                    data-testid="hu-gc-desc"
                    placeholder="What's this group about, who's it for, what do you do?" />

          <label className="hg-lbl">Tags (comma separated)</label>
          <input className="hg-input" value={tags}
                  onChange={e => setTags(e.target.value)} data-testid="hu-gc-tags"
                  placeholder="e.g. beginners, weekend, social" />

          {err && <div className="hg-err">{err}</div>}
          <button type="submit" disabled={busy} className="hg-btn"
                  data-testid="hu-gc-submit"
                  style={{ opacity: busy ? 0.5 : 1 }}>
            {busy ? <><Loader2 size={16} className="spin" /> Creating…</>
                  : <>Create my group <CheckCircle2 size={16} /></>}
          </button>
        </form>
      </main>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ────────── Detail ──────────
export function HustleUpGroupDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [g, setG] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const load = () => {
    setLoading(true);
    const t = TKN();
    Promise.all([
      api.get(`/api/hustleup/groups/${slug}` + (t ? `?token=${t}` : "")),
      api.get(`/api/hustleup/groups/${slug}/events?when=${tab === "past" ? "past" : "upcoming"}`),
    ]).then(([gr, ev]) => {
      setG(gr.data); setEvents(ev.data || []); setLoading(false);
    }).catch(e => { setErr(e?.response?.data?.detail || e.message); setLoading(false); });
  };

  useEffect(load, [slug, tab]);

  const join = async () => {
    if (!TKN()) { navigate("/hustleup/join"); return; }
    await api.post(`/api/hustleup/groups/${slug}/join?token=${TKN()}`);
    load();
  };
  const leave = async () => {
    if (!confirm("Leave this group?")) return;
    await api.post(`/api/hustleup/groups/${slug}/leave?token=${TKN()}`);
    load();
  };
  const toggleFav = async () => {
    if (!TKN()) { navigate("/hustleup/join"); return; }
    await api.post(`/api/hustleup/favourites/toggle?token=${TKN()}`, {
      kind: "group", target: slug });
    load();
  };

  if (loading) return (
    <div className="hg"><div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
      <Loader2 className="spin" /> Loading…</div>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
  if (err || !g) return (
    <div className="hg"><div style={{ padding: 60, textAlign: "center" }}>{err || "Not found"}</div>
      <style>{css}</style>
    </div>
  );

  return (
    <div className="hg" data-testid="hu-group-detail">
      <header className="hg-top">
        <a href="/hustleup/groups" className="hg-back"><ArrowLeft size={14} /> Groups</a>
        <button onClick={toggleFav}
                className={`hg-fav ${g.is_favourite ? "active" : ""}`}
                data-testid="hu-fav-toggle">
          <Star size={20} fill={g.is_favourite ? "currentColor" : "none"} />
        </button>
      </header>
      <main className="hg-main">
        <span className="hg-pill" style={{ marginBottom: 12, display: "inline-block" }}>
          {g.category}
        </span>
        <h1 className="hg-h1" data-testid="hu-group-name">{g.name}</h1>
        <div className="hg-cmeta" style={{ fontSize: 14, marginBottom: 16 }}>
          <MapPin size={14} /> {g.region}
          <span style={{ color: "#cbd5e1" }}>•</span>
          <Users size={14} /> {g.member_count} members
          <span style={{ color: "#cbd5e1" }}>•</span>
          {g.hosts?.length || 0} host{g.hosts?.length === 1 ? "" : "s"}
        </div>
        <p style={{ color: "#1f2937", lineHeight: 1.65, marginBottom: 24 }}>
          {g.description}
        </p>

        {g.is_member
          ? <button onClick={leave} className="hg-btn"
                    style={{ background: "white", color: "#0f172a",
                             border: "1px solid #e8e2d4" }}
                    data-testid="hu-group-leave">
              You're in this group · tap to leave
            </button>
          : <button onClick={join} className="hg-btn" data-testid="hu-group-join">
              Join group <Plus size={16} />
            </button>}

        {g.is_host && (
          <button onClick={() =>
            navigate(`/hustleup/groups/${slug}/events/new`)}
                  className="hg-btn"
                  style={{ marginTop: 12, background: "#fbbf24", color: "#0f172a" }}
                  data-testid="hu-event-new">
            <Plus size={16} /> Create event
          </button>
        )}

        <div className="hg-tabs" style={{ marginTop: 28 }}>
          <button className={`hg-tab ${tab === "upcoming" ? "active" : ""}`}
                  onClick={() => setTab("upcoming")} data-testid="hu-tab-upcoming">
            Upcoming
          </button>
          <button className={`hg-tab ${tab === "past" ? "active" : ""}`}
                  onClick={() => setTab("past")} data-testid="hu-tab-past">
            Past
          </button>
        </div>

        {events.length === 0
          ? <div style={{ textAlign: "center", padding: 40, color: "#64748b" }}>
              No {tab} events
            </div>
          : events.map((e, idx) => (
              <button key={e.event_id || e._id || idx}
                      onClick={() => navigate(`/hustleup/event/${e.event_id || e._id}`)}
                      className="hg-event" style={{ width: "100%", border: "1px solid #fde68a", background: "#fffbeb", textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit" }}
                      data-testid={`hu-event-${e.event_id || e._id || idx}`}>
                <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#92400e", fontWeight: 700, marginBottom: 6 }}>
                  <Calendar size={12} /> {new Date(e.starts_at).toLocaleString("en-AU", { weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" })}
                </div>
                <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 4 }}>{e.title}</div>
                <div style={{ fontSize: 12, color: "#475569" }}>
                  {e.is_online ? "Online" : (e.venue_name || "TBA")}
                  {" · "}
                  {e.rsvp_yes} going{e.capacity ? ` / ${e.capacity}` : ""}
                  {e.rsvp_waitlist > 0 && ` · ${e.rsvp_waitlist} waitlist`}
                </div>
              </button>
            ))}
      </main>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ────────── Event detail (RSVP + attendees + ICS) ──────────
export function HustleUpEventPage() {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [e, setE] = useState(null);
  const [att, setAtt] = useState({ accepted: [], waitlisted: [], summary: {} });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const load = () => {
    const t = TKN();
    Promise.all([
      api.get(`/api/hustleup/events/${eventId}` + (t ? `?token=${t}` : "")),
      api.get(`/api/hustleup/events/${eventId}/attendees`),
    ]).then(([ev, at]) => { setE(ev.data); setAtt(at.data); })
      .catch(e2 => setErr(e2?.response?.data?.detail || e2.message));
  };
  useEffect(load, [eventId]);

  const rsvp = async (state) => {
    if (!TKN()) { navigate("/hustleup/join"); return; }
    setBusy(true);
    try {
      await api.post(`/api/hustleup/events/${eventId}/rsvp?state=${state}&token=${TKN()}`);
      load();
    } catch (e2) { setErr(e2?.response?.data?.detail || e2.message); }
    setBusy(false);
  };

  if (err) return <div className="hg" style={{ padding: 40 }}>{err}<style>{css}</style></div>;
  if (!e) return <div className="hg" style={{ padding: 40 }}><Loader2 className="spin" /> Loading…
    <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style></div>;

  const icsUrl = `${API}/api/hustleup/events/${eventId}/calendar.ics`;

  return (
    <div className="hg" data-testid="hu-event-root">
      <header className="hg-top">
        <a href={`/hustleup/groups/${e.group_slug}`} className="hg-back">
          <ArrowLeft size={14} /> {e.group_name}
        </a>
      </header>
      <main className="hg-main">
        <h1 className="hg-h1" data-testid="hu-event-title">{e.title}</h1>
        <div style={{ fontSize: 14, color: "#475569", marginBottom: 18,
                      display: "flex", flexDirection: "column", gap: 8 }}>
          <div><Calendar size={14} /> {new Date(e.starts_at).toLocaleString("en-AU", { weekday: "long", day: "numeric", month: "long", hour: "numeric", minute: "2-digit" })}</div>
          {e.is_online
            ? <div><ExternalLink size={14} /> Online — link revealed after RSVP</div>
            : <div><MapPin size={14} /> {e.venue_name || "TBA"}{e.venue_addr ? ` · ${e.venue_addr}` : ""}</div>}
          <div><Users size={14} /> {att.summary.accepted || 0} going{e.capacity ? ` / ${e.capacity}` : ""}{att.summary.waitlisted ? ` · ${att.summary.waitlisted} on waitlist` : ""}</div>
        </div>

        <p style={{ color: "#1f2937", lineHeight: 1.65, marginBottom: 20 }}>
          {e.description}
        </p>

        {e.my_rsvp === "accepted" && (
          <div style={{ background: "#dcfce7", color: "#166534", padding: 16, borderRadius: 12, marginBottom: 12 }}>
            <CheckCircle2 size={16} /> You're going.{" "}
            <a href={icsUrl} style={{ color: "#0f172a", fontWeight: 700 }}
                data-testid="hu-event-ics">Add to calendar (.ics) →</a>
          </div>
        )}
        {e.my_rsvp === "waitlisted" && (
          <div style={{ background: "#fef3c7", color: "#92400e", padding: 16, borderRadius: 12, marginBottom: 12 }}>
            <Hourglass size={16} /> You're on the waitlist — we'll
            promote you the moment someone cancels.
          </div>
        )}
        {(!e.my_rsvp || e.my_rsvp === "cancelled") && (
          <button onClick={() => rsvp("accepted")} disabled={busy}
                  className="hg-btn" data-testid="hu-rsvp-yes">
            {busy ? <Loader2 size={16} className="spin" /> : "RSVP — count me in"}
          </button>
        )}
        {e.my_rsvp && e.my_rsvp !== "cancelled" && (
          <button onClick={() => rsvp("cancelled")} disabled={busy}
                  className="hg-btn"
                  style={{ background: "white", color: "#0f172a", border: "1px solid #e8e2d4", marginTop: 10 }}
                  data-testid="hu-rsvp-cancel">
            Cancel my RSVP
          </button>
        )}

        <h3 style={{ marginTop: 28, fontSize: 13, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 800 }}>
          Who's going ({att.summary.accepted || 0})
        </h3>
        <div className="hg-attlist" data-testid="hu-attendees">
          {(att.accepted || []).map(a => (
            <div key={a.member_id} className="hg-avatar" title={a.first_name}>
              {(a.first_name || "?")[0]?.toUpperCase()}
            </div>
          ))}
        </div>

        {(att.summary.waitlisted || 0) > 0 && <>
          <h3 style={{ marginTop: 20, fontSize: 13, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 800 }}>
            Waitlist ({att.summary.waitlisted})
          </h3>
          <div className="hg-attlist">
            {(att.waitlisted || []).map(a => (
              <div key={a.member_id} className="hg-avatar" title={a.first_name}
                    style={{ background: "#fef3c7", color: "#92400e" }}>
                {(a.first_name || "?")[0]?.toUpperCase()}
              </div>
            ))}
          </div>
        </>}
      </main>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}

// ────────── Event create (host only) ──────────
export function HustleUpEventCreatePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [venueName, setVenueName] = useState("");
  const [venueAddr, setVenueAddr] = useState("");
  const [capacity, setCapacity] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await api.post(`/api/hustleup/events?token=${TKN()}`, {
        group_slug: slug, title, description,
        starts_at: new Date(startsAt).toISOString(),
        ends_at:   endsAt ? new Date(endsAt).toISOString() : null,
        venue_name: venueName || null, venue_addr: venueAddr || null,
        capacity: capacity ? parseInt(capacity, 10) : null,
        is_online: false,
      });
      navigate(`/hustleup/event/${r.data.event_id}`);
    } catch (e2) { setErr(e2?.response?.data?.detail || e2.message); setBusy(false); }
  };

  return (
    <div className="hg" data-testid="hu-event-create-root">
      <header className="hg-top">
        <a href={`/hustleup/groups/${slug}`} className="hg-back">
          <ArrowLeft size={14} /> Back
        </a>
      </header>
      <main className="hg-main">
        <h1 className="hg-h1">Create event</h1>
        <p className="hg-sub">Set a date, a venue, and an optional capacity.
          We auto-handle the waitlist if you fill up.</p>
        <form onSubmit={submit}>
          <label className="hg-lbl">Title</label>
          <input className="hg-input" required minLength={4} value={title}
                  onChange={e => setTitle(e.target.value)} data-testid="hu-ec-title" />
          <label className="hg-lbl">Description</label>
          <textarea className="hg-area" required value={description}
                    onChange={e => setDescription(e.target.value)}
                    data-testid="hu-ec-desc" />
          <label className="hg-lbl">Starts at</label>
          <input className="hg-input" required type="datetime-local"
                  value={startsAt} onChange={e => setStartsAt(e.target.value)}
                  data-testid="hu-ec-start" />
          <label className="hg-lbl">Ends at (optional)</label>
          <input className="hg-input" type="datetime-local" value={endsAt}
                  onChange={e => setEndsAt(e.target.value)} data-testid="hu-ec-end" />
          <label className="hg-lbl">Venue name</label>
          <input className="hg-input" value={venueName}
                  onChange={e => setVenueName(e.target.value)} data-testid="hu-ec-vn" />
          <label className="hg-lbl">Venue address</label>
          <input className="hg-input" value={venueAddr}
                  onChange={e => setVenueAddr(e.target.value)} data-testid="hu-ec-va" />
          <label className="hg-lbl">Capacity (optional, enables waitlist)</label>
          <input className="hg-input" type="number" min="1" value={capacity}
                  onChange={e => setCapacity(e.target.value)} data-testid="hu-ec-cap" />
          {err && <div className="hg-err">{err}</div>}
          <button type="submit" disabled={busy} className="hg-btn"
                  data-testid="hu-ec-submit">
            {busy ? <><Loader2 size={16} className="spin" /> Creating…</>
                  : <>Create event <Calendar size={16} /></>}
          </button>
        </form>
      </main>
      <style>{css + ".spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}"}</style>
    </div>
  );
}
